package top.wcpe.allincore.dsl.compiler.theme

import kotlin.math.roundToLong

/**
 * 主题修饰器：把 `modifiers { compact = true; highContrast = true }` 声明的可组合修饰作用到
 * token 表上（对齐 Ant Design 的 `algorithm: [darkAlgorithm, compactAlgorithm]` 组合）。
 *
 * - **compact**：几何类 token（圆角 / 间距 / 控件高度 / 滑块 / 状态层 / 排版字号）按比例缩小，
 *   得到更密集的布局。
 * - **highContrast**：状态层不透明度翻倍、边框推向前景色，增强可读对比。
 *
 * 两者正交、可叠加。修饰是「合理规则」而非复刻某套手写主题——现有 Md3*Compact / HighContrast
 * 是手工逐值设计、不遵循单一规则，故保留原文件；本修饰器面向「新主题以 extends + modifiers
 * 快速派生紧凑/高对比变体」的场景。纯函数，present 无关（直接变换已有几何/状态层值）。
 */
object ThemeModifier {

    /** 紧凑模式几何缩放因子。 */
    private const val COMPACT_SCALE = 0.7

    /** 紧凑模式排版字号缩放因子（字号比几何收得轻，保证可读）。 */
    private const val COMPACT_TYPOGRAPHY_SCALE = 0.9

    /** 受 compact 缩放的几何 token（不含 borderWidth：紧凑下边框保持清晰）。 */
    private val COMPACT_GEOMETRY_KEYS = setOf(
        "cornerRadiusXSmall", "cornerRadiusSmall", "cornerRadiusMedium", "cornerRadiusLarge", "cornerRadiusExtraLarge",
        "radiusSm", "radiusMd", "radiusLg",
        "spaceXs", "spaceSm", "spaceMd", "spaceLg", "spaceXl",
        "controlHeightSM", "controlHeightMD", "controlHeightLG",
        "sliderTrackHeight", "sliderThumbSize", "stateLayerSize", "textPaddingX", "buttonIconSpacing",
    )

    /** 受 highContrast 增强的状态层不透明度 token。 */
    private val STATE_LAYER_OPACITY_KEYS = setOf(
        "stateLayerHoverOpacity", "stateLayerFocusOpacity", "stateLayerPressedOpacity", "stateLayerDraggedOpacity",
    )

    /** 主题是否声明了任一修饰。 */
    fun hasModifier(tokens: Map<String, Any>): Boolean =
        isOn(tokens["modifiers.compact"]) || isOn(tokens["modifiers.highContrast"])

    /** 应用修饰，返回变换后的新表；无修饰时原样返回。 */
    fun apply(tokens: LinkedHashMap<String, Any>): LinkedHashMap<String, Any> {
        val compact = isOn(tokens["modifiers.compact"])
        val highContrast = isOn(tokens["modifiers.highContrast"])
        if (!compact && !highContrast) return tokens

        val out = LinkedHashMap(tokens)
        if (compact) applyCompact(out)
        if (highContrast) applyHighContrast(out)
        return out
    }

    private fun applyCompact(out: LinkedHashMap<String, Any>) {
        for (key in COMPACT_GEOMETRY_KEYS) {
            val v = num(out[key]) ?: continue
            out[key] = roundGeometry(v * COMPACT_SCALE)
        }
        // 排版字号：遍历 typography*Size 缩放
        for (key in out.keys.toList()) {
            if (key.startsWith("typography") && key.endsWith("Size")) {
                val v = num(out[key]) ?: continue
                out[key] = roundGeometry(v * COMPACT_TYPOGRAPHY_SCALE)
            }
        }
    }

    private fun applyHighContrast(out: LinkedHashMap<String, Any>) {
        for (key in STATE_LAYER_OPACITY_KEYS) {
            val v = num(out[key]) ?: continue
            out[key] = (v * 2.0).coerceAtMost(1.0)
        }
        // 边框推向前景色，得到最高对比描边
        (out["onSurface"] as? String)?.let { out["outline"] = it }
    }

    /** 几何值四舍五入取整且不小于 0（token 统一存 Double）。roundToLong 双端同 half-up 语义（FR-UI-061）。 */
    private fun roundGeometry(x: Double): Double = x.roundToLong().toDouble().coerceAtLeast(0.0)

    private fun isOn(v: Any?): Boolean = v == true || v == "true"

    private fun num(v: Any?): Double? = when (v) {
        is Double -> v
        is Int -> v.toDouble()
        is Float -> v.toDouble()
        is Long -> v.toDouble()
        is String -> v.toDoubleOrNull()
        else -> null
    }
}
